import { useCallback, useEffect, useState } from "react";

import {
  getEnabled,
  getPermission,
  isSupported,
  notify,
  requestPermission as askForPermission,
  setEnabled as writeEnabled,
  subscribePrefChange,
  type DesktopPermission,
} from "@/lib/desktop-notify";

/**
 * Read-write hook for the desktop-notification preference + browser permission.
 * State is kept in sync across hook instances via window events emitted from
 * lib/desktop-notify.ts, so the bell toggle and any other consumer agree.
 *
 * The hook does NOT call notify() itself — that's a fire-and-forget function
 * called from the WS handler (useNotificationSocket). This hook is only for
 * UI that needs to *display or change* the preference.
 */
export function useDesktopNotifications() {
  const [permission, setPermission] = useState<DesktopPermission>(() => getPermission());
  const [enabled,    setEnabledState] = useState<boolean>(() => getEnabled());

  useEffect(() => {
    return subscribePrefChange(() => {
      setPermission(getPermission());
      setEnabledState(getEnabled());
    });
  }, []);

  const toggle = useCallback(async (on: boolean): Promise<DesktopPermission> => {
    if (on) {
      // Turning on: first ensure the browser will let us. If permission is
      // "default", prompt; if "denied", we can't recover from JS — the
      // user has to flip the site permission in browser settings.
      const result = await askForPermission();
      if (result === "granted") {
        writeEnabled(true);
        // Fire a confirmation pop immediately so the user gets *visual*
        // proof the pipeline works — the bg-tab gate is bypassed for this
        // one because the user is necessarily focused on the bell when
        // they toggle it on.
        notify({
          title: "Desktop notifications are on",
          body: "You'll see pop-ups here when something needs you.",
          tag: "kms-notify-test",
          force: true,
        });
      } else {
        writeEnabled(false);
      }
      return result;
    }
    writeEnabled(false);
    return getPermission();
  }, []);

  /** Fire a test pop. Same bypass as the toggle-on confirmation. */
  const sendTest = useCallback(() => {
    notify({
      title: "Test notification",
      body: "If you can see this, desktop notifications are working.",
      tag: "kms-notify-test",
      force: true,
    });
  }, []);

  return {
    supported: isSupported(),
    permission,
    enabled,
    toggle,
    sendTest,
  };
}
